import {h, Fragment} from "preact"

import { useState, useEffect } from "preact/hooks";
import { SerpElement, useCurrencyFormatting } from "@nosto/preact";
import { productImagePlaceholder } from "../helpers";
import ProductBadge from "./ProductBadge";
import { extractShopifyHandle } from "./utils/extractShopifyHandle";
import { translatedText } from "./utils/translatedText";


function setUrl(fullUrl) {
  // Normalize root to not have leading or trailing slashes for later concatenation
  const root = window?.Shopify?.routes?.root || "/";
  const cleanRoot = root.replace(/^\/|\/$/g, "");

  // Try to extract path starting from `/products/...`
  const match = fullUrl.match(/\/products\/.+$/);
  const productPath = match
    ? match[0]
    : (() => {
        // fallback: slice from 4th segment onward
        const parts = fullUrl.split("/");
        return "/" + parts.slice(3).join("/");
      })();

  return cleanRoot ? `/${cleanRoot}${productPath}` : productPath;
}
  
function handleQuickShopClick(url) {
  const shortUrl = setUrl(url);

  window.dispatchEvent(new CustomEvent("quickshop", { detail: shortUrl }));
  window.dispatchEvent(new Event("closemodal"));
  window.dispatchEvent(new Event("cart-hide"));

  if (typeof Tracking?.quickShopClick === "function") {
    Tracking.quickShopClick("quickshop-");
  }
} 

export default function Product({ product, index}) {
  const [hover, setHover] = useState(false);
  const { format } = useCurrencyFormatting();

  // --- IMAGE SOURCES ---
  const secondImage = product.imageUrl || productImagePlaceholder;
  const urls = product.alternateImageUrls ?? [];

  const override = product.customFields.find(f => f.key === "custom-featured_image_override")?.value?.trim().replace(".", "");
  const isOverrideValid = override && override !== "No override" && !override.endsWith(".mp4");

  const getImage = (key) => urls.find(url => url.includes(key)) || "";
  const defaultKey = "_003";

  // --- CUSTOM FEATURED IMAGE OVERRIDE ---
  const fallback = getImage(defaultKey) || urls[0] || "";
  const overrideImage = isOverrideValid ? getImage(override) : "";

  // --- IMAGE MATCHING LOGIC ---
  const firstImage = (overrideImage || fallback) !== secondImage
    ? (overrideImage || fallback)
    : null; 

  return (
    <li 
      className="nosto-product-info grid__item flex flex-col gap-8 relative"
      x-nosto-handle={extractShopifyHandle(product?.url)}
    >

      {/* <button
        type="button"
        aria-label="Add to wishlist"
        className="absolute top-12 right-12 z-10 btn-wishlist" 
        dangerouslySetInnerHTML={{
          __html: `
            <div 
              x-data="buttonWishlist"
              x-init="prodHandle = '${extractShopifyHandle(product?.url)}'"
              @set-button.window="setButton($event.detail)"
              x-ref="button"
              x-transition
              button-wishlist
              x-bind:data-product-handle="${extractShopifyHandle(product?.url)}"
              data-product-handle="${extractShopifyHandle(product?.url)}"
            >
              <style scoped>
                .icon {
                  fill: transparent;
                  stroke: #000000;
                  transition: fill 0.3s ease;
                }
                .active .icon {
                  fill: #000000;
                  stroke: #000000;
                }
              </style>
              <svg class="icon icon-heart" width="14" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="m7.329 12.97-.33-.206-.331.206-5.463 3.404V1.125h11.586v15.249L7.33 12.97Z" stroke="#000" stroke-width="1.25"></path>
              </svg>
            </div>
          `,
        }}
      /> */}

 
      <SerpElement
        as="a"
        hit={product}
        ariaLabel={`Product ${product.name}`}
        className="nosto-product-url product-link"
      >
        <div
          className="relative overflow-hidden aspect-2-3 mb-8 lg:mb-12 bg-image bg-cover bg-center"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <ProductBadge product={product} />

          {firstImage && secondImage ? (
            <>
              <img
                src={firstImage}
                alt={product.name}
                className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-300 ${
                  hover ? "opacity-0" : "opacity-100"
                }`}
              />
              <img
                src={secondImage}
                alt={`${product.name} alternate view`}
                className={`absolute inset-0 w-full h-full object-cover object-center ${
                  hover
                    ? "object-contain object-center secondary_image special-image z-[1] opacity-95 mix-blend-multiply"
                    : "opacity-0"
                }`} 
              />
            </>
          ) : (
            <img
              src={secondImage}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-contain object-center z-[1] opacity-95 mix-blend-multiply"
            />
          )}
          
          <div class={`fibbl-bar-wrapper-${product.productId || index} absolute pointer-events-none left-0 bottom-0 z-10`}>
            <fibbl-bar
              data-product-id={`${extractShopifyHandle(product?.url)}`}
              class="bg-transparent gap-12 px-8 lg:px-12 pt-8 lg:pt-8 pb-16 lg:pb-24"
            >
              <button
                data-element="fibbl-3d-quick-view"
                class="fibbl-button-primary pointer-events-auto plp"
              >
                <span>3D</span>
              </button>

              <button
                data-element="fibbl-qr-code"
                data-type="vto"
                class="fibbl-button-secondary pointer-events-auto whitespace-nowrap"
              >
                <span>
                {translatedText("tryOn")}
                </span>
              </button>
            </fibbl-bar> 
          </div>
        </div>

        <div className="flex justify-between gap-8 flex-col" data-nosto-element="product">
            <div className="flex flex-col justify-between gap-4 text-13-120 w-full">
                <h3 className="lg:col-span-6 normal-case">{product.name?.split(" / ")[0]}</h3>
                <div aria-label="Price" class="nosto-product-price price__sale flex gap-8 text-13-120 ">
                  <span class="nosto-newprice money">{format(product.price)}</span>
                  {product.listPrice && product.listPrice > product.price && (
                      <s class="nosto-oldprice money price-item price-item--regular opacity-[.55]">{format(product.listPrice)}</s>
                  )}
                </div>
            </div>
        </div>
      </SerpElement>

      


      <button 
            className="btn black uppercase btn-icon plus w-fit" 
            data-scroll-label="Quick shop" 
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleQuickShopClick(product.url);
              }}
            >
                <span>Quick shop</span>
            </button>
    </li>
  );
}
